import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useHostAuth } from '../shared/ui';

/**
 * The signed-in user's distributor.
 *
 * The Angular portal loaded this into an NgRx `auth.company` slice from an
 * effect on login. Apollo's cache does the same job — one query, shared by
 * every screen that asks for it — so the store goes and this stays.
 *
 * Looked up by uuid because that is what the Auth0 `company` claim carries;
 * the numeric id the quote queries filter on comes back with the record.
 */
export const DISTRIBUTOR = gql`
  query PortalDistributor($uuid: ID!) {
    distributorByUuid(uuid: $uuid) {
      id
      uuid
      name
      gp
      distributorPortalGp
      fireRatingGp
      distributorPortalFireRatingGp
      fireRatingEnabled
      multiTierEnabled
      mezzViewerAppEnabled
      newQuoteDocumentEnabled
      emailAddress
      phoneNumber
      logoUrl
      address1
      address2
      address3
      city
      county
      postCode
      country
    }
  }
`;

export interface Distributor {
  id: number;
  uuid: string;
  name: string;
  gp: number;
  distributorPortalGp: number;
  fireRatingGp: number;
  distributorPortalFireRatingGp: number;
  fireRatingEnabled: boolean;
  /** Whether this distributor may quote a floor with more than one deck. */
  multiTierEnabled: boolean;
  /** Gates ancillary positioning, the staircase context menu and drawing settings. */
  mezzViewerAppEnabled: boolean;
  /**
   * Whether this distributor's quotation is the document iQD renders rather
   * than the Word one Prime builds. Switched on one distributor at a time.
   */
  newQuoteDocumentEnabled: boolean;
  emailAddress: string;
  phoneNumber: string;
  logoUrl: string;
}

export function useDistributor(): Distributor | null {
  const { user } = useHostAuth();
  const { data } = useQuery(DISTRIBUTOR, {
    variables: { uuid: user?.companyUuid },
    skip: !user?.companyUuid,
  });
  return (data as any)?.distributorByUuid ?? null;
}
